import { SvelteMap } from "svelte/reactivity"
import {
  fetchWorkOrderTable,
  type WorkOrderTableProjection,
} from "../../../../../../api/kehrblatt/work-orders"
import {
  buildWorkOrderSelectionFromProjection,
  isWorkOrderSelectableForAppointmentCreate,
  type WorkOrderSelectSelection,
} from "../../../_logic/work-orders-selection"

export type WorkOrderSelectSearchMode = "default" | "appointment-create"

export type WorkOrderSelectItem = {
  value: string
  label: string
  subLabel: string
  metaLabel: string
  selection: WorkOrderSelectSelection
}

export const searchLimit = 25
const idLoadChunkSize = 100
const selectionCacheByOrganization = new SvelteMap<
  string,
  SvelteMap<string, WorkOrderSelectSelection>
>()
const inflightLoadsById = new Map<string, Promise<WorkOrderSelectSelection[]>>()
const inflightSearchesByKey = new Map<string, Promise<WorkOrderSelectSelection[]>>()
const searchResultIdsByKey = new Map<string, string[]>()

export const normalizeId = (input: string | null | undefined) => `${input ?? ""}`.trim()
export const normalizeSearchInput = (input: string) => input.trim()

const cacheForOrganization = (organizationId: string) => {
  const organizationKey = normalizeId(organizationId)
  let cache = selectionCacheByOrganization.get(organizationKey)

  if (!cache) {
    cache = new SvelteMap<string, WorkOrderSelectSelection>()
    selectionCacheByOrganization.set(organizationKey, cache)
  }

  return cache
}

const loadKeyForId = (organizationId: string, workOrderId: string) =>
  `${normalizeId(organizationId)}:${normalizeId(workOrderId)}`

const searchKey = (
  organizationId: string,
  input: string,
  limit: number,
  searchMode: WorkOrderSelectSearchMode,
  requiredAccess: "view" | "manage",
  employeeId: string | null | undefined,
  propertyId: string | null | undefined,
) =>
  [
    normalizeId(organizationId),
    normalizeSearchInput(input),
    limit,
    searchMode,
    requiredAccess,
    normalizeId(employeeId),
    normalizeId(propertyId),
  ].join(":")

const cacheSelections = (organizationId: string, incoming: WorkOrderSelectSelection[]) => {
  if (!normalizeId(organizationId) || !incoming.length) return

  const cache = cacheForOrganization(organizationId)
  for (const selection of incoming) {
    const workOrderId = normalizeId(selection.id)

    if (!workOrderId) continue

    cache.set(workOrderId, selection)
  }
}

export const getCachedWorkOrderSelection = (
  organizationId: string,
  workOrderId: string | null | undefined,
) => {
  const organizationKey = normalizeId(organizationId)
  const normalizedWorkOrderId = normalizeId(workOrderId)

  if (!organizationKey || !normalizedWorkOrderId) return null

  return selectionCacheByOrganization.get(organizationKey)?.get(normalizedWorkOrderId) ?? null
}

const selectionsFromProjection = (
  projection: WorkOrderTableProjection,
  searchMode: WorkOrderSelectSearchMode = "default",
  employeeId: string | null | undefined = null,
) => {
  const propertyById = new Map(
    (projection.properties ?? []).map((property) => [normalizeId(property.id), property]),
  )
  const scheduleEvents = projection.schedule_events ?? []

  return (projection.items ?? [])
    .filter(
      (workOrder) =>
        searchMode !== "appointment-create" ||
        isWorkOrderSelectableForAppointmentCreate({ workOrder, scheduleEvents, employeeId }),
    )
    .map((workOrder) =>
      buildWorkOrderSelectionFromProjection({
        workOrder,
        property: propertyById.get(normalizeId(workOrder.property_id)) ?? null,
        scheduleEvents,
      }),
    )
}

const uniqueIds = (ids: Array<string | null | undefined>) => {
  const result: string[] = []
  const seen = new Set<string>()
  for (const id of ids) {
    const normalized = normalizeId(id)

    if (!normalized || seen.has(normalized)) continue

    seen.add(normalized)
    result.push(normalized)
  }

  return result
}

const chunks = <T>(items: T[], size: number) => {
  const result: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size))
  }

  return result
}

export const primeWorkOrderSelectionCache = async ({
  organizationId,
  workOrderIds,
}: {
  organizationId: string
  workOrderIds: Array<string | null | undefined>
}) => {
  const organizationKey = normalizeId(organizationId)
  const requestedIds = uniqueIds(workOrderIds)

  if (!organizationKey || !requestedIds.length) return []

  const cached = requestedIds
    .map((id) => getCachedWorkOrderSelection(organizationKey, id))
    .filter((selection): selection is WorkOrderSelectSelection => Boolean(selection))
  const missingIds = requestedIds.filter((id) => !getCachedWorkOrderSelection(organizationKey, id))
  const pendingPromises = missingIds
    .map((id) => inflightLoadsById.get(loadKeyForId(organizationKey, id)))
    .filter((promise): promise is Promise<WorkOrderSelectSelection[]> => Boolean(promise))
  const idsNeedingLoad = missingIds.filter(
    (id) => !inflightLoadsById.has(loadKeyForId(organizationKey, id)),
  )

  const loadPromises = chunks(idsNeedingLoad, idLoadChunkSize).map((workOrderIdsChunk) => {
    const promise = fetchWorkOrderTable({
      organizationId: organizationKey,
      workOrderIds: workOrderIdsChunk,
      limit: workOrderIdsChunk.length,
    }).then((projection) => {
      const selections = selectionsFromProjection(projection)
      cacheSelections(organizationKey, selections)

      return selections
    })

    for (const id of workOrderIdsChunk) {
      const key = loadKeyForId(organizationKey, id)
      inflightLoadsById.set(key, promise)
      promise.then(
        () => {
          if (inflightLoadsById.get(key) === promise) inflightLoadsById.delete(key)
        },
        () => {
          if (inflightLoadsById.get(key) === promise) inflightLoadsById.delete(key)
        },
      )
    }

    return promise
  })

  const loaded = (await Promise.all([...pendingPromises, ...loadPromises])).flat()

  return [...cached, ...loaded]
}

export const searchWorkOrderSelections = async ({
  organizationId,
  input,
  limit = searchLimit,
  searchMode = "default",
  requiredAccess = "view",
  employeeId = null,
  propertyId = null,
}: {
  organizationId: string
  input: string
  limit?: number
  searchMode?: WorkOrderSelectSearchMode
  requiredAccess?: "view" | "manage"
  employeeId?: string | null
  propertyId?: string | null
}) => {
  const organizationKey = normalizeId(organizationId)

  if (!organizationKey) return []

  const scopedEmployeeId = normalizeId(employeeId)
  const scopedPropertyId = normalizeId(propertyId)
  const key = searchKey(
    organizationKey,
    input,
    limit,
    searchMode,
    requiredAccess,
    scopedEmployeeId,
    scopedPropertyId,
  )
  const cachedIds = searchResultIdsByKey.get(key)

  if (cachedIds) {
    return cachedIds
      .map((id) => getCachedWorkOrderSelection(organizationKey, id))
      .filter((selection): selection is WorkOrderSelectSelection => Boolean(selection))
  }

  const existing = inflightSearchesByKey.get(key)

  if (existing) return existing

  const promise = fetchWorkOrderTable({
    ...(requiredAccess === "manage" ? { requiredAccess } : {}),
    organizationId: organizationKey,
    propertyId: scopedPropertyId || undefined,
    searchQuery: normalizeSearchInput(input) || undefined,
    status: "OPEN",
    ...(searchMode === "appointment-create"
      ? {
          appointmentAvailable: true,
          ...(scopedEmployeeId ? { appointmentEmployeeId: scopedEmployeeId } : {}),
        }
      : {}),
    sortBy: "created_at",
    sortDir: "desc",
    limit,
  }).then((projection) => {
    const selections = selectionsFromProjection(projection, searchMode, scopedEmployeeId)
    cacheSelections(organizationKey, selections)
    searchResultIdsByKey.set(
      key,
      selections.map((selection) => normalizeId(selection.id)).filter(Boolean),
    )

    return selections
  })

  inflightSearchesByKey.set(key, promise)
  promise.then(
    () => {
      if (inflightSearchesByKey.get(key) === promise) inflightSearchesByKey.delete(key)
    },
    () => {
      if (inflightSearchesByKey.get(key) === promise) inflightSearchesByKey.delete(key)
    },
  )

  return promise
}

export const resetWorkOrderSelectionCacheForTests = () => {
  selectionCacheByOrganization.clear()
  inflightLoadsById.clear()
  inflightSearchesByKey.clear()
  searchResultIdsByKey.clear()
}

export const mapSelectionToItem = (selection: WorkOrderSelectSelection): WorkOrderSelectItem => ({
  value: normalizeId(selection.id),
  label: selection.label,
  subLabel: selection.propertyLabel,
  metaLabel: selection.unitLabel,
  selection,
})

export const uniqueSelections = (selections: WorkOrderSelectSelection[]) => {
  const result: WorkOrderSelectSelection[] = []
  const seen = new Set<string>()
  for (const selection of selections) {
    const id = normalizeId(selection.id)

    if (!id || seen.has(id)) continue

    seen.add(id)
    result.push(selection)
  }

  return result
}
